import { Edit, Delete, NorthEast, SouthWest } from "@mui/icons-material";
import { Box } from "@mui/material";

import { TableActions } from "../../components/v3";
import { formatBalance, getBalanceColor } from "../../utils";

/**
 * Route paths for the client module.
 * Single source of truth — reuse these instead of hard-coding `/clients...`, `/client...`, `/add-income...`,
 * `/edit-income...` or `/receivables...` strings.
 */
export const CLIENT_ROUTES = {
  root: "/clients",
  details: "/clients/:id",
  invoice: "/client/invoice/:id",
  receipt: "/client/receipt/:id",
  addIncome: "/add-income",
  editIncome: "/edit-income/:incomeId",
  addReceivable: "/receivables/add",
  editReceivable: "/receivables/edit/:id",
} as const;

const buildClientActionsColumn = (handleActions, hasEditPayment, hasDeletePayment, hasEditIncome, hasDeleteIncome) => ({
  field: "actions",
  headerName: "Actions",
  flex: 1,
  sortable: false,
  renderCell: (params) => {
    const { type } = params.row;
    const actions = [];

    if (type === "clientReceivable") {
      if (hasEditPayment) {
        actions.push({
          icon: Edit,
          type: "edit",
          label: "Edit",
          onClick: handleActions,
        });
      }

      if (hasDeletePayment) {
        actions.push({
          icon: Delete,
          type: "delete",
          label: "Delete",
          onClick: handleActions,
        });
      }
    } else if (type === "income") {
      if (hasEditIncome) {
        actions.push({
          icon: Edit,
          type: "edit",
          label: "Edit",
          onClick: handleActions,
        });
      }

      if (hasDeleteIncome) {
        actions.push({
          icon: Delete,
          type: "delete",
          label: "Delete",
          onClick: handleActions,
        });
      }
    }
    return <TableActions item={params} actions={actions} />;
  },
});

export const ClientTableColumns = [
  { field: "sl", headerName: "Sl No", flex: 0.4, sortable: false },
  {
    field: "name",
    headerName: "Name",
    flex: 1.5,
    sortable: true,
  },
  {
    field: "primaryPhone",
    headerName: "Mobile",
    flex: 1.5,
    sortable: false,
  },
  {
    field: "balance",
    headerName: "Balance",
    flex: 1.5,
    sortable: false,
    renderCell: (params) => {
      const invertBalance = (balance) => -(Number(balance) || 0);
      const value = formatBalance(invertBalance(params.value));
      return (
        <Box sx={{ color: getBalanceColor(invertBalance(params.value)), display: "flex", alignItems: "center" }}>{value}</Box>
      );
    },
  },
];

/**
 * Route paths for the client module.
 * Single source of truth — reuse these instead of hard-coding `/clients...`, `/client...`, `/add-income`,
 * `/edit-income...` and `/receivables...` strings.
 */
export const getStatementTableColumns = (handleActions, hasEditPayment, hasDeletePayment, hasEditIncome, hasDeleteIncome) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    {
      field: "date",
      headerName: "date",
      flex: 1,
      sortable: true,
      sortingOrder: ["desc", "asc", null],
    },
    {
      field: "projectOrAccount",
      headerName: "project / account",
      flex: 1.5,
      sortable: false,
    },
    {
      field: "description",
      headerName: "description",
      flex: 1.5,
      sortable: false,
    },
    {
      field: "amount",
      headerName: "amount",
      flex: 1,
      sortable: true,
      renderCell: (params) => {
        const value = params.value;
        const { type } = params.row;

        if (!value) return "-";

        const IconComponent = type === "income" ? SouthWest : type === "clientReceivables" ? NorthEast : NorthEast;
        const color = type === "income" ? "green" : type === "clientReceivables" ? "red" : "red";

        return (
          <span style={{ color, display: "flex", alignItems: "center", gap: 4 }}>
            <IconComponent fontSize="small" />₹ {Number(value).toFixed(2)}
          </span>
        );
      },
    },
  ];
  if (hasEditPayment || hasDeletePayment || hasEditIncome || hasDeleteIncome) {
    headers.push(buildClientActionsColumn(handleActions, hasEditPayment, hasDeletePayment, hasEditIncome, hasDeleteIncome));
  }
  return headers;
};

export const getLedgerTableColumns = (handleActions, hasEditPayment, hasDeletePayment, hasEditIncome, hasDeleteIncome) => {
  const headers = [
    { field: "sl", headerName: "Sl No", flex: 0.5, sortable: false },
    { field: "date", headerName: "date", flex: 1, sortable: false },
    { field: "projectOrAccount", headerName: "project / account", flex: 1.5, sortable: false },
    { field: "description", headerName: "description", flex: 1.5, sortable: false },
    {
      field: "amount",
      headerName: "amount",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const value = params.value;
        const { type } = params.row;

        if (!value) return "-";

        const IconComponent = type === "income" ? SouthWest : type === "clientReceivables" ? NorthEast : NorthEast;
        const color = type === "income" ? "green" : type === "clientReceivables" ? "red" : "red";

        return (
          <span style={{ color, display: "flex", alignItems: "center", gap: 4 }}>
            <IconComponent fontSize="small" />₹ {Number(value).toFixed(2)}
          </span>
        );
      },
    },
    {
      field: "balance",
      headerName: "Balance",
      flex: 1.5,
      sortable: false,
      renderCell: (params) => {
        const rawBalance = params.value;
        if (rawBalance === null || rawBalance === undefined || rawBalance === "") return "-";
        // Invert the stored balance for display, to match formatBalance and the client list column.
        const num = -(Number(rawBalance) || 0);
        const formatted = `${num < 0 ? "-" : ""}₹ ${Math.abs(num).toFixed(2)}`;
        return <Box sx={{ color: getBalanceColor(num), display: "flex", alignItems: "center" }}>{formatted}</Box>;
      },
    },
  ];

  if (hasEditPayment || hasDeletePayment || hasEditIncome || hasDeleteIncome) {
    headers.push(buildClientActionsColumn(handleActions, hasEditPayment, hasDeletePayment, hasEditIncome, hasDeleteIncome));
  }

  return headers;
};
